import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { FeatureFlagProvider } from './contexts/FeatureFlagContext';
import { BulkImportProvider } from './contexts/BulkImportContext';
import { WebSocketProvider } from './contexts/WebSocketContext';
import { PresenceProvider } from './contexts/PresenceContext';
import Layout from './components/Layout';
import PartsListPage from './pages/PartsListPage';

import TaxonomyPage from './pages/TaxonomyPage';
import InventoryPage from './pages/InventoryPage';
import StockInquiryPage from './pages/StockInquiryPage';
import LocationManagementPage from './pages/LocationManagementPage';
import LotManagementPage from './pages/LotManagementPage';
import GRNManagementPage from './pages/GRNManagementPage';
import IssueTransferPage from './pages/IssueTransferPage';
import CycleCountPage from './pages/CycleCountPage';
import QuarantineManagementPage from './pages/QuarantineManagementPage';
import NCRManagementPage from './pages/NCRManagementPage';
import QCInspectorDashboard from './pages/QCInspectorDashboard';
import InventoryManagerDashboard from './pages/InventoryManagerDashboard';
import AdjustmentPage from './pages/AdjustmentPage';
import LoginPage from './pages/LoginPage';
import SettingsPage from './pages/SettingsPage';
import UserSettingsPage from './pages/UserSettingsPage';
import UsersPage from './pages/UsersPage';
import UserDetailPage from './pages/UserDetailPage';
import AcceptInvitationPage from './pages/AcceptInvitationPage';
import RolePermissionsPage from './pages/RolePermissionsPage';
import DatabaseExplorerPage from './pages/DatabaseExplorerPage';
import BomExplorerPage from './pages/BomExplorerPage';
import ProductLibraryPage from './pages/ProductLibraryPage';
import BomEditorPage from './pages/BomEditorPage';
import MaterialProfilesPage from './pages/MaterialProfilesPage';
import RawMaterialLibraryPage from './pages/RawMaterialLibraryPage';
import ProcessLibraryPage from './pages/ProcessLibraryPage';
import FastenersLibraryPage from './pages/FastenersLibraryPage';
import ApprovalDashboard from './pages/ApprovalDashboard';
import RemnantInventoryPage from './pages/RemnantInventoryPage';
import { ImportStatusDrawer } from './components/bulk-import';

/**
 * Protected Route Wrapper
 * Redirects to login if user is not authenticated
 */
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-cyan-500"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    // Save the location they were trying to go to
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
};

/**
 * Public Route Wrapper
 * Redirects to home if user is already authenticated
 */
const PublicRoute = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-cyan-500"></div>
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return children;
};

/**
 * Main App Routes
 */
const AppRoutes = () => {
  return (
    <Routes>
      {/* Public Routes */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />
      
      {/* Accept Invitation - Public route (no auth required) */}
      <Route
        path="/accept-invitation"
        element={<AcceptInvitationPage />}
      />

      {/* Protected Routes */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Layout>
              <PartsListPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/parts"
        element={
          <ProtectedRoute>
            <Layout>
              <PartsListPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/taxonomy"
        element={
          <ProtectedRoute>
            <Layout>
              <TaxonomyPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/inventory"
        element={
          <ProtectedRoute>
            <Layout>
              <InventoryPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/inventory/qc-dashboard"
        element={
          <ProtectedRoute>
            <Layout>
              <QCInspectorDashboard />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/inventory/manager-dashboard"
        element={
          <ProtectedRoute>
            <Layout>
              <InventoryManagerDashboard />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/inventory/stock"
        element={
          <ProtectedRoute>
            <Layout>
              <StockInquiryPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/inventory/locations"
        element={
          <ProtectedRoute>
            <Layout>
              <LocationManagementPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/inventory/lots"
        element={
          <ProtectedRoute>
            <Layout>
              <LotManagementPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/inventory/receiving"
        element={
          <ProtectedRoute>
            <Layout>
              <GRNManagementPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/inventory/issue-transfer"
        element={
          <ProtectedRoute>
            <Layout>
              <IssueTransferPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/inventory/cycle-counts"
        element={
          <ProtectedRoute>
            <Layout>
              <CycleCountPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/inventory/quarantine"
        element={
          <ProtectedRoute>
            <Layout>
              <QuarantineManagementPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/inventory/ncr"
        element={
          <ProtectedRoute>
            <Layout>
              <NCRManagementPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/inventory/adjustments"
        element={
          <ProtectedRoute>
            <Layout>
              <AdjustmentPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      
      {/* User Settings Routes - Available to all authenticated users */}
      <Route
        path="/settings/profile"
        element={
          <ProtectedRoute>
            <Layout>
              <UserSettingsPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      
      {/* Admin Settings Routes */}
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <Layout>
              <SettingsPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/users"
        element={
          <ProtectedRoute>
            <Layout>
              <UsersPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      
      {/* Admin Routes */}
      <Route
        path="/admin/user"
        element={
          <ProtectedRoute>
            <Layout>
              <UsersPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/user/:userId"
        element={
          <ProtectedRoute>
            <Layout>
              <UserDetailPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/permissions"
        element={
          <ProtectedRoute>
            <Layout>
              <RolePermissionsPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      
      {/* Database Explorer - Admin Tool */}
      <Route
        path="/db-explorer"
        element={
          <ProtectedRoute>
            <DatabaseExplorerPage />
          </ProtectedRoute>
        }
      />

      {/* Product Library (V2 — standalone viewport, no Layout wrapper) */}
      <Route
        path="/product-library"
        element={
          <ProtectedRoute>
            <ProductLibraryPage />
          </ProtectedRoute>
        }
      />
      {/* Legacy /bom route redirects to new path */}
      <Route
        path="/bom"
        element={<Navigate to="/product-library" replace />}
      />
      <Route
        path="/bom/:bomId"
        element={
          <ProtectedRoute>
            <BomEditorPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/bom/material-profiles"
        element={
          <ProtectedRoute>
            <Layout>
              <RawMaterialLibraryPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/bom/approvals"
        element={
          <ProtectedRoute>
            <Layout>
              <ApprovalDashboard />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/bom/remnant-inventory"
        element={
          <ProtectedRoute>
            <RemnantInventoryPage />
          </ProtectedRoute>
        }
      />

      {/* Standalone Library Routes */}
      <Route
        path="/bom/raw-material"
        element={
          <ProtectedRoute>
            <RawMaterialLibraryPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/bom/fabrication"
        element={
          <ProtectedRoute>
            <ProcessLibraryPage domain="fabrication" />
          </ProtectedRoute>
        }
      />
      <Route
        path="/bom/surface"
        element={
          <ProtectedRoute>
            <ProcessLibraryPage domain="surface_treatment" />
          </ProtectedRoute>
        }
      />
      <Route
        path="/bom/fasteners"
        element={
          <ProtectedRoute>
            <FastenersLibraryPage />
          </ProtectedRoute>
        }
      />

      {/* Catch all - redirect to home */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <Router>
      <AuthProvider>
        <WebSocketProvider>
          <PresenceProvider>
            <FeatureFlagProvider>
              <BulkImportProvider>
                <Toaster position="bottom-right" richColors expand={true} />
                <AppRoutes />
                <ImportStatusDrawer />
              </BulkImportProvider>
            </FeatureFlagProvider>
          </PresenceProvider>
        </WebSocketProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;
